import { ArrowRight, Compass, Globe, Handshake, Sparkles, TrendingUp, Workflow } from "lucide-react";
import { BTN_PRIMARY, SECTION_LABEL } from "@/lib/site";
import { track } from "@/lib/track";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";

const SERVICES = [
    {
        icon: Compass,
        title: "AI Strategy & Consulting",
        problem: "You know AI could help — but not where to start.",
        desc: "Practical guidance on where AI and automation genuinely pay off in your business, with a clear, prioritized plan before any build begins.",
        id: "ai-strategy",
    },
    {
        icon: Workflow,
        title: "AI Workflow Automation",
        problem: "Repetitive tasks quietly eat your team's week.",
        desc: "Automate enquiry handling, lead qualification, follow-ups and routine internal workflows, so your team spends time on work that actually moves the business.",
        id: "ai-automation",
    },
    {
        icon: Globe,
        title: "Custom Websites & Web Applications",
        problem: "Your website should win business, and your tools should fit how you operate.",
        desc: "Responsive, conversion-focused websites plus dashboards, portals, booking systems and internal tools built around your process — not the other way round.",
        id: "web-apps",
    },
    {
        icon: Sparkles,
        title: "AI-Powered Business Solutions",
        problem: "Some problems need more than an off-the-shelf tool.",
        desc: "Purpose-built solutions that apply AI to real business needs — from smarter customer communication to faster internal decisions.",
        id: "ai-solutions",
    },
    {
        icon: TrendingUp,
        title: "Process Optimization & Digital Transformation",
        problem: "Manual steps and disconnected tools slow everything down.",
        desc: "We map how work actually flows, remove bottlenecks and digitize the steps that matter — without disrupting what already works.",
        id: "process-optimization",
    },
    {
        icon: Handshake,
        title: "White-Label AI Development for Agencies",
        problem: "Client demand grows faster than your team can hire.",
        desc: "Additional AI and development capacity for digital agencies, delivered quietly under your own brand with clear deliverables and direct communication.",
        id: "white-label",
    },
];

export default function Services() {
    return (
        <section
            id="services"
            className="relative scroll-mt-20 overflow-hidden bg-[#F7F0EA]"
            data-testid="services-section"
        >
            <div
                className="pointer-events-none absolute -right-32 top-20 h-[460px] w-[460px] rounded-full bg-[#CF4242]/[0.07] blur-3xl"
                aria-hidden="true"
            />
            <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
                <Reveal>
                    <p className={SECTION_LABEL}>Services</p>
                    <h2 className="mt-4 max-w-3xl font-heading text-3xl font-medium tracking-tight text-[#1C1917] md:text-4xl lg:text-[3.25rem] lg:leading-[1.1]">
                        Digital solutions built around{" "}
                        <span className="font-serif italic text-[#CF4242]">your business.</span>
                    </h2>
                </Reveal>

                <Stagger className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {SERVICES.map((s, i) => (
                        <StaggerItem key={s.id} className="h-full">
                            <article
                                data-testid={`service-card-${s.id}`}
                                className="spotlight group relative flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-[#EDE3DA] bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[transform,box-shadow] duration-500 ease-out hover:-translate-y-2 hover:shadow-[0_32px_60px_-24px_rgb(28,25,23,0.35)]"
                            >
                                <span
                                    className="absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r from-[#CF4242] via-[#E8764A] to-[#CF4242] transition-transform duration-500 group-hover:scale-x-100"
                                    aria-hidden="true"
                                />
                                <span
                                    className="absolute right-6 top-5 font-heading text-5xl font-semibold text-[#1C1917]/[0.04] transition-colors duration-500 group-hover:text-[#CF4242]/10"
                                    aria-hidden="true"
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-[#FBECE9] ring-1 ring-[#CF4242]/10 transition-[background-color,transform] duration-500 group-hover:-rotate-6 group-hover:bg-[#CF4242]">
                                    <s.icon
                                        size={22}
                                        className="text-[#CF4242] transition-colors duration-500 group-hover:text-white"
                                        aria-hidden="true"
                                    />
                                </span>
                                <h3 className="relative mt-6 font-heading text-xl font-semibold leading-snug text-[#1C1917] md:text-2xl">
                                    {s.title}
                                </h3>
                                <p className="relative mt-3 text-sm font-semibold text-[#1C1917]">
                                    {s.problem}
                                </p>
                                <p className="relative mt-2 text-sm leading-relaxed text-[#57504A] md:text-base">
                                    {s.desc}
                                </p>
                            </article>
                        </StaggerItem>
                    ))}
                </Stagger>

                <Reveal className="mt-14">
                    <a
                        href="#contact"
                        data-testid="services-estimate-btn"
                        onClick={() => track("cta_click", { cta: "services_get_estimate" })}
                        className={BTN_PRIMARY}
                    >
                        Get a Project Estimate <ArrowRight size={16} />
                    </a>
                </Reveal>
            </div>
        </section>
    );
}
